/**
 * Round DMS-UAT-1 (2026) — Issue 7.
 *
 * Legacy DMSNew stored Product Info as six parallel MULTILINE text columns
 * on DmsEcr (partDescription, partNumber, drawingNumber, customerPartNumber,
 * currentRev, newRev): line N of each column belongs to the same part line.
 * The modern UI rendered each column as one lump, which UAT flagged as
 * unreadable and unordered.
 *
 * These helpers parse the stored strings into ordered row objects for
 * display/editing, and serialize rows back into the SAME six columns
 * (newline-joined) so no schema change is needed and legacy data keeps
 * round-tripping safely. A ';' is treated as a secondary line delimiter on
 * PARSE only (seen in some migrated rows) but is never written back.
 */

export const PRODUCT_INFO_FIELDS = [
    { key: "partDescription",    label: "Part Description / Name" },
    { key: "partNumber",         label: "Part Number" },
    { key: "drawingNumber",      label: "Drawing Number" },
    { key: "customerPartNumber", label: "Customer Part Number" },
    { key: "currentRev",         label: "Current Rev" },
    { key: "newRev",             label: "New Rev" },
];

function splitLines(value) {
    if (value === null || value === undefined) return [];
    return String(value)
        .replace(/\r\n/g, "\n")
        .replace(/\r/g, "\n")
        .replace(/;/g, "\n")
        .split("\n")
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
}

/**
 * parseProductInfoRows({ partDescription, partNumber, ... }) →
 *   [{ partDescription, partNumber, drawingNumber, customerPartNumber,
 *      currentRev, newRev }, ...]
 * Rows are aligned by line index and preserve the original entry order.
 */
export function parseProductInfoRows(productInfo) {
    const cols = PRODUCT_INFO_FIELDS.map((f) => splitLines(productInfo?.[f.key]));
    const rowCount = Math.max(0, ...cols.map((c) => c.length));
    const rows = [];
    for (let i = 0; i < rowCount; i++) {
        const row = {};
        PRODUCT_INFO_FIELDS.forEach((f, colIdx) => {
            row[f.key] = cols[colIdx][i] || "";
        });
        rows.push(row);
    }
    return rows;
}

/**
 * serializeProductInfoRows(rows) → { partDescription, partNumber, ... }
 * Joins each column back with "\n". Fully-empty rows are dropped.
 */
export function serializeProductInfoRows(rows) {
    const kept = (rows || []).filter((r) =>
        PRODUCT_INFO_FIELDS.some((f) => (r[f.key] || "").trim().length > 0)
    );
    const out = {};
    PRODUCT_INFO_FIELDS.forEach((f) => {
        out[f.key] = kept.map((r) => (r[f.key] || "").trim()).join("\n");
    });
    return out;
}

export default parseProductInfoRows;
